import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Grid,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Alert,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    Search as SearchIcon,
    ExpandMore as ExpandMoreIcon,
    Download as DownloadIcon,
    Clear as ClearIcon,
    FilterList as FilterIcon
} from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import PageHeader from '../../components/common/PageHeader';
import userListingService from '../../services/userListingService';

// Configurer dayjs pour le français
dayjs.locale('fr');

/**
 * Page de recherche avancée des relations User ↔ Listings
 */
const UserListingsSearchPage = () => {
    // États des filtres de recherche
    const [searchParams, setSearchParams] = useState({
        userName: '',
        userEmail: '',
        listingTitle: '',
        listingStatus: '',
        dateFrom: null,
        dateTo: null,
        page: 1,
        limit: 10
    });

    // États des résultats
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [hasSearched, setHasSearched] = useState(false);

    // État de pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    /**
     * Mettre à jour les paramètres de recherche
     */
    const handleParamChange = (field, value) => {
        setSearchParams(prev => ({
            ...prev,
            [field]: value
        }));
    };

    /**
     * Effectuer la recherche
     */
    const handleSearch = async () => {
        try {
            setLoading(true);
            setError(null);
            setHasSearched(true);

            const params = {
                ...searchParams,
                page: page + 1,
                limit: rowsPerPage,
                dateFrom: searchParams.dateFrom ? searchParams.dateFrom.format('YYYY-MM-DD') : null,
                dateTo: searchParams.dateTo ? searchParams.dateTo.format('YYYY-MM-DD') : null
            };

            // Filtrer les paramètres vides
            const filteredParams = Object.fromEntries(
                Object.entries(params).filter(([_, value]) => value !== null && value !== '')
            );

            const response = await userListingService.searchUserListings(filteredParams);
            
            setSearchResults(response.data || []);
            setTotalCount(response.pagination?.totalItems || 0);
        } catch (err) {
            console.error('Erreur lors de la recherche:', err);
            setError('Erreur lors de la recherche');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Réinitialiser la recherche
     */
    const handleReset = () => {
        setSearchParams({
            userName: '',
            userEmail: '',
            listingTitle: '',
            listingStatus: '',
            dateFrom: null,
            dateTo: null,
            page: 1,
            limit: 10
        });
        setSearchResults([]);
        setHasSearched(false);
        setError(null);
        setPage(0);
    };

    /**
     * Exporter les résultats
     */
    const handleExport = async (format) => {
        try {
            const exportParams = {
                format,
                filters: {
                    ...searchParams,
                    dateFrom: searchParams.dateFrom ? searchParams.dateFrom.format('YYYY-MM-DD') : null,
                    dateTo: searchParams.dateTo ? searchParams.dateTo.format('YYYY-MM-DD') : null
                }
            };

            const blob = await userListingService.exportUserListings(exportParams);
            
            // Créer un lien de téléchargement
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `user-listings-search.${format}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Erreur lors de l\'export:', err);
            setError('Erreur lors de l\'export');
        }
    };

    /**
     * Gérer le changement de page
     */
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    /**
     * Gérer le changement du nombre de lignes par page
     */
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    /**
     * Effectuer une nouvelle recherche lors du changement de pagination
     */
    useEffect(() => {
        if (hasSearched) {
            handleSearch();
        }
    }, [page, rowsPerPage]);

    /**
     * Formater la date d'affichage
     */
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    /**
     * Obtenir la couleur du chip selon le statut
     */
    const getStatusColor = (status) => {
        switch (status) {
            case 'active':
                return 'success';
            case 'pending':
                return 'warning';
            case 'rejected':
                return 'error';
            case 'suspended':
                return 'default';
            default:
                return 'primary';
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <PageHeader
                    title="Recherche avancée User ↔ Listings"
                    subtitle="Recherchez et filtrez les relations entre utilisateurs et annonces"
                    icon={<SearchIcon />}
                />

                {/* Formulaire de recherche */}
                <Accordion defaultExpanded sx={{ mb: 3 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                            <FilterIcon sx={{ mr: 1 }} />
                            Critères de recherche
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Paper sx={{ p: 3 }}>
                            <Grid container spacing={3}>
                                {/* Recherche utilisateur */}
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Nom d'utilisateur"
                                        variant="outlined"
                                        value={searchParams.userName}
                                        onChange={(e) => handleParamChange('userName', e.target.value)}
                                        placeholder="Rechercher par nom d'utilisateur..."
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Email utilisateur"
                                        variant="outlined"
                                        value={searchParams.userEmail}
                                        onChange={(e) => handleParamChange('userEmail', e.target.value)}
                                        placeholder="Rechercher par email..."
                                    />
                                </Grid>

                                {/* Recherche annonce */}
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Titre d'annonce"
                                        variant="outlined"
                                        value={searchParams.listingTitle}
                                        onChange={(e) => handleParamChange('listingTitle', e.target.value)}
                                        placeholder="Rechercher par titre d'annonce..."
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Statut d'annonce</InputLabel>
                                        <Select
                                            value={searchParams.listingStatus}
                                            label="Statut d'annonce"
                                            onChange={(e) => handleParamChange('listingStatus', e.target.value)}
                                        >
                                            <MenuItem value="">Tous les statuts</MenuItem>
                                            <MenuItem value="active">Active</MenuItem>
                                            <MenuItem value="pending">En attente</MenuItem>
                                            <MenuItem value="rejected">Rejetée</MenuItem>
                                            <MenuItem value="suspended">Suspendue</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Filtres de date */}
                                <Grid item xs={12} md={6}>
                                    <DatePicker
                                        label="Date de début"
                                        value={searchParams.dateFrom}
                                        onChange={(date) => handleParamChange('dateFrom', date)}
                                        slotProps={{ textField: { fullWidth: true } }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <DatePicker
                                        label="Date de fin"
                                        value={searchParams.dateTo}
                                        onChange={(date) => handleParamChange('dateTo', date)}
                                        slotProps={{ textField: { fullWidth: true } }}
                                    />
                                </Grid>

                                {/* Boutons d'action */}
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                        <Button
                                            variant="outlined"
                                            onClick={handleReset}
                                            startIcon={<ClearIcon />}
                                        >
                                            Réinitialiser
                                        </Button>
                                        <Button
                                            variant="contained"
                                            onClick={handleSearch}
                                            startIcon={<SearchIcon />}
                                            disabled={loading}
                                        >
                                            {loading ? 'Recherche...' : 'Rechercher'}
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </AccordionDetails>
                </Accordion>

                {/* Résultats de recherche */}
                {hasSearched && (
                    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6">
                                Résultats de recherche ({totalCount} trouvé{totalCount > 1 ? 's' : ''})
                            </Typography>
                            {searchResults.length > 0 && (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        size="small"
                                        onClick={() => handleExport('csv')}
                                        startIcon={<DownloadIcon />}
                                    >
                                        CSV
                                    </Button>
                                    <Button
                                        size="small"
                                        onClick={() => handleExport('excel')}
                                        startIcon={<DownloadIcon />}
                                    >
                                        Excel
                                    </Button>
                                </Box>
                            )}
                        </Box>

                        {error && (
                            <Alert severity="error" sx={{ m: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                <TableContainer sx={{ maxHeight: 600 }}>
                                    <Table stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Utilisateur</TableCell>
                                                <TableCell>Email</TableCell>
                                                <TableCell>Annonce</TableCell>
                                                <TableCell>Statut</TableCell>
                                                <TableCell>Prix</TableCell>
                                                <TableCell>Catégorie</TableCell>
                                                <TableCell>Date création</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {searchResults.map((relation) => (
                                                <TableRow 
                                                    key={`${relation.user_id}-${relation.listing_id}`}
                                                    hover
                                                >
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {relation.user_first_name} {relation.user_last_name}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{relation.user_email}</TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                            {relation.listing_title}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={relation.listing_status}
                                                            color={getStatusColor(relation.listing_status)}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {relation.listing_price ? `${relation.listing_price}€` : 'N/A'}
                                                    </TableCell>
                                                    <TableCell>{relation.category_name}</TableCell>
                                                    <TableCell>
                                                        {formatDate(relation.listing_created_at)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {searchResults.length === 0 && !loading && (
                                                <TableRow>
                                                    <TableCell colSpan={7} align="center">
                                                        <Typography variant="body2" color="text.secondary">
                                                            Aucun résultat trouvé pour ces critères
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {searchResults.length > 0 && (
                                    <TablePagination
                                        rowsPerPageOptions={[5, 10, 25, 50]}
                                        component="div"
                                        count={totalCount}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        labelRowsPerPage="Lignes par page"
                                        labelDisplayedRows={({ from, to, count }) => 
                                            `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`
                                        }
                                    />
                                )}
                            </>
                        )}
                    </Paper>
                )}
            </Container>
        </LocalizationProvider>
    );
};

export default UserListingsSearchPage;
